// ============================================================================
// bunchaBullshiiiiSystem.ts — GOOP ZONE "Buncha Bullshiiii" System (v3)
// Manages in-run inventory (NO limit), applies stat modifiers, triggers effects
// Items found from chests, vendors, shrines — NOT from level-ups
// Lives in: client/src/game/bunchaBullshiiiiSystem.ts
// ============================================================================

import {
  BullshiiiiItem,
  ItemRarity,
  ItemSource,
  StatModifier,
  ItemEffect,
  TriggerType,
  BuffDimension,
  getBullshiiiiById,
  getActiveBullshiiii,
  rollRandomBullshiiii,
  getBullshiiiiBySource,
  BUNCHA_BULLSHIIII,
} from './bunchaBullshiiii';

// --- Player Stats Interface ---

export interface PlayerStats {
  // Base stats (before item modifiers)
  baseMaxHp: number;
  baseDamage: number;
  baseArmor: number;
  baseMoveSpeed: number;
  baseAttackSpeed: number;
  baseAttackRange: number;
  baseCritChance: number;
  baseLuck: number;
  baseXpGain: number;
  basePickupRange: number;
  baseDodgeChance: number;
  baseAccuracy: number;
  baseDamageReduction: number;
  baseHpRegen: number;
  // BETA 16.36 — shield regen rate (HP/sec to shield, after 5s damage delay)
  baseShieldRegen: number;
  // BETA 16.37 — PSBOT cycle-30i §7D Bug A: critDamage was declared on
  // E04 Hutch's Horseshoe III + others but had NO apply path. Add
  // critDamage to the stat pipeline so item-side `stat: 'critDamage'`
  // modifiers actually flow to the player.
  baseCritDamage: number;

  // Computed stats (after item modifiers)
  maxHp: number;
  currentHp: number;
  overheal: number;
  damage: number;
  gold: number;             // BETA 19.98 — gold multiplier from items (1.0 = no bonus, 1.15 = +15%)
  armor: number;
  moveSpeed: number;
  attackSpeed: number;
  attackRange: number;
  critChance: number;
  luck: number;
  xpGain: number;
  pickupRange: number;
  dodgeChance: number;
  accuracy: number;
  damageReduction: number;
  hpRegen: number;
  shieldRegen: number;
  critDamage: number;     // BETA 16.37 — see baseCritDamage above
  difficulty: number;
}

// --- Inventory Slot ---

export interface InventorySlot {
  item: BullshiiiiItem;
  stacks: number;
  /** BETA 20.78 — luck scales item buff VALUES (Dylan: "items not really
   *  having higher buffs depending on luck"). This is the SUM of the luck
   *  multiplier captured at EACH pickup (snapshot per stack), so non-luck
   *  numbers grow with the luck you had when you grabbed each copy and
   *  earlier stacks keep their own pickup-time value. Equals `stacks` when
   *  every pickup was at luck 0 (mult 1.0); undefined = legacy slot (falls
   *  back to `stacks` = old unscaled behavior). See _itemLuckMult below. */
  luckMultSum?: number;
  /** BETA 16.29 — PSBOT cycle-30e: per-dimension pick counter. When the
   *  item has buffDimensions, each duplicate pickup commits to ONE
   *  dimension (rolled at offer time + locked when player picks). The
   *  count of picks PER dimension lives here so the apply path can sum
   *  bonusPerStack × pickedDimensions[statField] for each dimension.
   *  Empty for legacy items (no buffDimensions defined). */
  pickedDimensions?: Record<string, number>;
}

// --- Runtime Effect State ---

export interface SillyJuiceState {
  currentKillStacks: number;
  maxKillStacks: number;
}

export interface NuttyTBagState {
  distanceTraveled: number;
  summonThreshold: number;
}

// --- Buncha Bullshiiii System State ---

export interface BullshiiiiSystemState {
  /** All items the player has collected this run — NO LIMIT */
  inventory: InventorySlot[];
  /** Player stats (base + computed) */
  playerStats: PlayerStats;
  /** Set of item IDs unlocked on this account (from meta-progression) */
  unlockedItemIds: Set<string>;
  /** Set of item IDs banished this run (won't appear in future drops) */
  banishedItemIds: Set<string>;
  // Runtime effect states
  sillyJuiceState: SillyJuiceState;
  nuttyTBagState: NuttyTBagState;
  chatToxicityLevel: number;
  // Invulnerability
  isInvulnerable: boolean;
  invulnerabilityTimer: number;
}

// --- Default Player Stats ---

export function createDefaultPlayerStats(): PlayerStats {
  return {
    // BETA 14.97 — user direction: base player stats per the Player Base
    // Stats spec. Max HP 100, HP Regen 10, Crit Chance 1%, Crit Damage +1×
    // (= 2.0× total), Evasion 1%, everything else 0/default. Reverts BETA 14.85
    // (which had set maxHp 150 to match engine.ts). engine.ts is updated to
    // match (100). Mismatch between these values causes the silent-revert bug.
    baseMaxHp: 100,
    baseDamage: 10,
    baseArmor: 0,
    baseMoveSpeed: 200,
    baseAttackSpeed: 1.0,
    baseAttackRange: 50,
    baseCritChance: 1,
    baseLuck: 0,
    baseXpGain: 1.0,
    basePickupRange: 40,
    baseDodgeChance: 0,
    baseAccuracy: 100,
    baseDamageReduction: 0,
    baseHpRegen: 10,
    baseShieldRegen: 0,    // BETA 16.36 — items can grant +shieldRegen
    baseCritDamage: 0,     // BETA 16.37 — items can grant +critDamage (E04, etc.)

    maxHp: 100,
    currentHp: 100,
    overheal: 0,
    damage: 10,
    armor: 0,
    moveSpeed: 200,
    attackSpeed: 1.0,
    attackRange: 50,
    critChance: 1,
    luck: 0,
    xpGain: 1.0,
    pickupRange: 40,
    dodgeChance: 0,
    accuracy: 100,
    damageReduction: 0,
    hpRegen: 10,
    shieldRegen: 0,
    critDamage: 0,
    difficulty: 1.0,
    gold: 1.0,            // BETA 19.98 — gold multiplier from items (1.0 = no bonus)
  };
}

// --- Initialize Buncha Bullshiiii System ---

/**
 * Create a fresh in-run Buncha Bullshiiii state.
 * @param unlockedItemIds - Set of item IDs unlocked on this account
 */
export function createBullshiiiiSystem(
  unlockedItemIds: Set<string> = new Set()
): BullshiiiiSystemState {
  // If no unlock set provided, default to all default-unlocked items
  if (unlockedItemIds.size === 0) {
    for (const item of BUNCHA_BULLSHIIII) {
      if (item.starterPool) {
        unlockedItemIds.add(item.id);
      }
    }
  }

  return {
    inventory: [],
    playerStats: createDefaultPlayerStats(),
    unlockedItemIds,
    banishedItemIds: new Set(),
    sillyJuiceState: { currentKillStacks: 0, maxKillStacks: 50 },
    nuttyTBagState: { distanceTraveled: 0, summonThreshold: 500 },
    chatToxicityLevel: 0,
    isInvulnerable: false,
    invulnerabilityTimer: 0,
  };
}

// --- Add Bullshiiii to Inventory (NO LIMIT) ---

export interface AddItemResult {
  success: boolean;
  message: string;
  isNewItem: boolean;
  stackCount: number;
}

// BETA 14.73 — BUG-6 fix: itemTracker for bot/real-game picks. Previously only
// WeaponLab's setItemStacks() called trackItemStackChange — chest picks via
// addBullshiiii silently bypassed the tracker, leaving itemTracker={} in BotFarm
// CSV runs. Same registration-callback pattern as registerShrineSelectTracker
// (avoids circular dep — engine.ts wires it on init).
type ItemStackChangeTracker = (itemId: string, newStack: number) => void;
let _itemStackChangeTracker: ItemStackChangeTracker | null = null;
export function registerItemStackChangeTracker(t: ItemStackChangeTracker | null): void {
  _itemStackChangeTracker = t;
}

/**
 * Add an item to the player's inventory.
 * There is NO inventory limit — items stack infinitely if stackable.
 */
// BETA 20.78 — the shared luck→value multiplier (mirror of peanutPercs
// luckValueMult; inlined to avoid an import cycle in this widely-imported
// file). ×1.0 at luck 0 → ×1.5 at 200 → ×2.0 at 400+ (hard ceiling). Keep
// the two copies in sync.
function _itemLuckMult(luck: number): number {
  return 1 + Math.min(Math.max(0, luck), 400) / 400;
}

export function addBullshiiii(state: BullshiiiiSystemState, itemId: string, rolledDimension?: BuffDimension, pickupLuck = 0, pickupJitter = 1): AddItemResult {
  const item = getBullshiiiiById(itemId);
  if (!item) {
    return { success: false, message: `Item ${itemId} not found`, isNewItem: false, stackCount: 0 };
  }
  // BETA 20.78 — snapshot luck at THIS pickup. BETA 21.00 — fold in the per-pick
  // jitter (default 1 = no fluctuation) so each pickup's buff varies, matching the
  // value the chest card showed. recalculateStats multiplies base × luckMultSum.
  const _luckMult = _itemLuckMult(pickupLuck) * pickupJitter;

  // Check if already in inventory
  const existingSlot = state.inventory.find(slot => slot.item.id === itemId);

  if (existingSlot) {
    // Stack existing item — no limit
    existingSlot.stacks++;
    // BETA 20.78 — accumulate this pickup's luck multiplier. Default the
    // prior sum to the pre-bump stack count (each old stack = mult 1.0) so
    // legacy slots upgrade cleanly.
    existingSlot.luckMultSum = (existingSlot.luckMultSum ?? (existingSlot.stacks - 1)) + _luckMult;
    // BETA 16.29 — PSBOT cycle-30e: if a dimension was rolled at offer
    // time AND the item has buffDimensions, increment that dimension's
    // pick count on the slot. Apply path reads pickedDimensions to
    // compute per-dimension contribution.
    if (rolledDimension && item.buffDimensions) {
      existingSlot.pickedDimensions = existingSlot.pickedDimensions ?? {};
      existingSlot.pickedDimensions[rolledDimension.statField] =
        (existingSlot.pickedDimensions[rolledDimension.statField] ?? 0) + 1;
    }
    recalculateStats(state);
    // BETA 14.73 BUG-6: fire item-tracker callback (engine.ts → trackItemStackChange).
    _itemStackChangeTracker?.(itemId, existingSlot.stacks);
    return { success: true, message: `${item.name} x${existingSlot.stacks}`, isNewItem: false, stackCount: existingSlot.stacks };
  }

  // Add new item — NO LIMIT CHECK
  // BETA 16.29 (REVISED 17.09) — first pickup now accepts a rolled
  // dimension too. Bug confirmed in PSBOT cycle-30aa: Killa run picked
  // UC06 once, finalArmor + finalEvasion both 0. Root cause was
  // itemsAdapter._pickChestItemsWithDimensions short-circuiting on
  // unowned items → first pick always undefined → pickedDimensions={}
  // → apply path saw 0 picks → 0 stat. Fixed at the offer side AND here:
  // if a dimension comes through for a first pick, seed the count to 1
  // so the apply path can read it.
  const newSlot: InventorySlot = { item, stacks: 1, luckMultSum: _luckMult }; // BETA 20.78 — first pickup's luck snapshot
  if (item.buffDimensions && item.buffDimensions.length > 0) {
    newSlot.pickedDimensions = {};
    if (rolledDimension) {
      newSlot.pickedDimensions[rolledDimension.statField] = 1;
    }
  }
  state.inventory.push(newSlot);
  recalculateStats(state);
  // BETA 14.73 BUG-6: fire item-tracker callback for first pick (stack=1).
  _itemStackChangeTracker?.(itemId, 1);
  return { success: true, message: `Picked up ${item.name}!`, isNewItem: true, stackCount: 1 };
}

// --- Remove Bullshiiii from Inventory ---

export function removeBullshiiii(state: BullshiiiiSystemState, itemId: string): boolean {
  const index = state.inventory.findIndex(slot => slot.item.id === itemId);
  if (index === -1) return false;

  state.inventory.splice(index, 1);
  recalculateStats(state);
  return true;
}

// --- Banish an Item ---

/**
 * Banish an item so it won't appear in future drops this run.
 * Useful for removing unwanted items from the drop pool.
 */
export function banishBullshiiii(state: BullshiiiiSystemState, itemId: string): boolean {
  const item = getBullshiiiiById(itemId);
  if (!item) return false;
  state.banishedItemIds.add(itemId);
  return true;
}

// --- Roll Items from Sources ---

export interface ItemOffer {
  item: BullshiiiiItem;
  /** GOOP cost to purchase from vendor (0 for free sources like boss drops) */
  goopCost: number;
  /** BETA 16.29 — PSBOT cycle-30e: when this offer is for an item the
   *  player ALREADY OWNS and the item has buffDimensions, this is the
   *  rolled dimension (locked at offer time). Card render shows
   *  `dimension.label: current → next`. On pickup, this dimension's
   *  pickedDimensions[statField] increments by 1. */
  rolledDimension?: BuffDimension;
}

/**
 * BETA 16.29 — PSBOT cycle-30e: weighted-random dimension roll. Picks ONE
 * BuffDimension from the item's buffDimensions list weighted by `weight`.
 * Returns null if item has no dimensions or empty list.
 */
export function rollItemDimension(item: BullshiiiiItem): BuffDimension | null {
  const dims = item.buffDimensions;
  if (!dims || dims.length === 0) return null;
  const totalWeight = dims.reduce((sum, d) => sum + d.weight, 0);
  if (totalWeight <= 0) return null;
  let r = Math.random() * totalWeight;
  for (const d of dims) {
    r -= d.weight;
    if (r <= 0) return d;
  }
  return dims[dims.length - 1]; // fallback (rounding)
}

/**
 * BETA 16.29 — Sum the player's bonus on a specific dimension across an
 * inventory slot. Reads slot.pickedDimensions[statField] (count of times
 * the dimension was picked) × dimension.bonusPerStack. Returns 0 if the
 * dimension wasn't picked or isn't defined on the item.
 */
export function getDimensionBonus(slot: InventorySlot, statField: string): number {
  if (!slot.item.buffDimensions || !slot.pickedDimensions) return 0;
  const dim = slot.item.buffDimensions.find(d => d.statField === statField);
  if (!dim) return 0;
  const picks = slot.pickedDimensions[statField] ?? 0;
  return picks * dim.bonusPerStack;
}

/**
 * Generate item offers for a chest.
 * Chests give 1 random item from the unlocked pool.
 * @param luck - Player's luck stat
 * @param isFreeChest - Whether this is a free chest (boss/elite drop) or paid
 */
// BETA 16.28 — PSBOT cycle-30c P0: filter owned non-stackable items + at-cap
// stackable items out of offer rolls. Per Dylan: "i already have bananas
// buncha bullshii item. it offered it to me again." Affects 8 non-stackable
// items (banana, voogoo, whoopie, sheeesh, iranian bird, dracs gas mask,
// timmy spray, liar deceiver) plus any stackable item that's hit maxStacks.
// Returns true if the item CAN still be picked up given current inventory.
// BETA 21.86 — exported so the LIVE 3-card chest path (itemsAdapter._pickChestItems)
// uses the SAME at-cap filter as the single-item rolls. Dylan: "if i'm at an item's
// max stacks it should be removed from the offer pool — another chance at something else."
export function isItemAvailableForPickup(
  state: BullshiiiiSystemState,
  item: BullshiiiiItem,
): boolean { return _isItemAvailableForPickup(state, item); }
function _isItemAvailableForPickup(
  state: BullshiiiiSystemState,
  item: BullshiiiiItem,
): boolean {
  // BETA 20.04 — Dylan-spec unlock filter: regular users only see items they've
  // unlocked. Dev users (DevPeanut etc.) have ALL items in unlockedItemIds via
  // applyDevUserUnlocks in goopSystem.ts:649, so they see everything naturally.
  // For non-dev users with an item NOT in their unlock set, this item is
  // hidden from chest/vendor/shrine/boss-drop pools — they have to satisfy
  // the unlockCondition (kill X enemies, take Y damage, etc.) first.
  if (!state.unlockedItemIds.has(item.id)) return false;
  const slot = state.inventory.find(s => s.item.id === item.id);
  if (!slot) return true;                          // not owned → available
  if (item.maxStacks <= 0) return true;            // unlimited stacks (post-cycle-30e)
  return slot.stacks < item.maxStacks;             // stackable + not at cap
}

// BETA 19.64 — PSBOT cycle-30ay: Dylan Viking run had C09 picked twice in
// 10 seconds, C01 picked twice. Anti-duplicate ring buffer remembers the
// last 3 chest picks; re-roll if we'd offer the same item again. Doesn't
// prevent it if the available pool is so small that the only available
// items are duplicates (in that case we accept the dupe after retries).
const _recentChestPicks: string[] = [];
// BETA 20.01 — anti-dupe ring buffer 3 → 8. With 43 active items and most runs
// picking 8-12, buffer of 3 only blocked 25% of the offered pool — Dylan's
// complaint: "same identical buncha bullshii items all the time." Wider buffer
// forces more variety per run.
const RECENT_PICKS_MAX = 14;  // BETA 22.05 — 8 → 14: ring now PERSISTS across runs (cross-run variety, itemsAdapter no longer wipes it), so it remembers more of the last run to avoid re-offering it
// BETA 20.03 — exported so the 3-card chest path (itemsAdapter._pickChestItems)
// can also use the cross-chest dedup buffer (was previously only used by the
// single-item rollChestItem path).
export function _wasRecentlyPicked(id: string): boolean { return _recentChestPicks.includes(id); }
export function _recordChestPick(id: string): void {
  _recentChestPicks.push(id);
  if (_recentChestPicks.length > RECENT_PICKS_MAX) _recentChestPicks.shift();
}
/** Cleared on new run (called from system reset). */
export function clearRecentChestPicks(): void { _recentChestPicks.length = 0; }

export function rollChestItem(
  state: BullshiiiiSystemState,
  luck: number = 0,
  isFreeChest: boolean = false
): ItemOffer {
  // BETA 16.28 — retry up to 20 attempts to land an available item before
  // falling through (worst case: pool all-owned, return last roll).
  // BETA 19.64 — Also retry if this item was offered in the last 3 chests
  // (anti-duplicate per cycle-30ay). Increases attempts to 30 to give the
  // anti-dupe filter more chances before falling through.
  let item = rollRandomBullshiiii(luck, state.unlockedItemIds, 'chest');
  let attempts = 0;
  while ((!_isItemAvailableForPickup(state, item) || _wasRecentlyPicked(item.id)) && attempts < 30) {
    item = rollRandomBullshiiii(luck, state.unlockedItemIds, 'chest');
    attempts++;
  }
  _recordChestPick(item.id);
  // Paid chests cost GOOP based on rarity
  const goopCost = isFreeChest ? 0 : getChestCost(item.rarity);
  // BETA 16.29 — PSBOT cycle-30e: if this is a duplicate of an OWNED
  // item with buffDimensions, roll one dimension and lock it on the
  // offer. Card render shows `dim.label: current → next`. On pickup,
  // pickedDimensions[statField] increments.
  const ownedSlot = state.inventory.find(s => s.item.id === item.id);
  const rolledDimension = (ownedSlot && item.buffDimensions && item.buffDimensions.length > 0)
    ? (rollItemDimension(item) ?? undefined)
    : undefined;
  return { item, goopCost, rolledDimension };
}

/**
 * Generate item offers for a vendor.
 * Vendors show 2-3 items to choose from.
 * @param luck - Player's luck stat
 * @param count - Number of items to offer (default 3)
 */
export function rollVendorItems(
  state: BullshiiiiSystemState,
  luck: number = 0,
  count: number = 3
): ItemOffer[] {
  const offers: ItemOffer[] = [];
  const usedIds = new Set<string>();

  for (let i = 0; i < count; i++) {
    let attempts = 0;
    let item: BullshiiiiItem;
    // BETA 16.28 — also reject owned non-stackables / at-cap stackables.
    do {
      item = rollRandomBullshiiii(luck, state.unlockedItemIds, 'vendor');
      attempts++;
    } while ((usedIds.has(item.id) || !_isItemAvailableForPickup(state, item)) && attempts < 20);

    usedIds.add(item.id);
    offers.push({
      item,
      goopCost: getVendorCost(item.rarity),
    });
  }

  return offers;
}

/**
 * Generate item offers for a shrine (Moai-style: choose 1 of 3).
 * @param luck - Player's luck stat
 */
export function rollShrineItems(
  state: BullshiiiiSystemState,
  luck: number = 0
): ItemOffer[] {
  const offers: ItemOffer[] = [];
  const usedIds = new Set<string>();

  for (let i = 0; i < 3; i++) {
    let attempts = 0;
    let item: BullshiiiiItem;
    // BETA 16.28 — also reject owned non-stackables / at-cap stackables.
    do {
      item = rollRandomBullshiiii(luck, state.unlockedItemIds, 'shrine');
      attempts++;
    } while ((usedIds.has(item.id) || !_isItemAvailableForPickup(state, item)) && attempts < 20);

    usedIds.add(item.id);
    offers.push({ item, goopCost: 0 }); // Shrines are free
  }

  return offers;
}

/**
 * Roll a free item from a boss or elite kill.
 */
export function rollBossDropItem(
  state: BullshiiiiSystemState,
  luck: number = 0,
  isElite: boolean = false
): ItemOffer {
  const source: ItemSource = isElite ? 'elite_drop' : 'boss_drop';
  // BETA 16.28 — retry until we land an available item.
  let item = rollRandomBullshiiii(luck + (isElite ? 5 : 15), state.unlockedItemIds, source);
  let attempts = 0;
  while (!_isItemAvailableForPickup(state, item) && attempts < 20) {
    item = rollRandomBullshiiii(luck + (isElite ? 5 : 15), state.unlockedItemIds, source);
    attempts++;
  }
  return { item, goopCost: 0 };
}

// --- GOOP Costs ---

/** Get the GOOP cost to open a paid chest based on rarity */
function getChestCost(rarity: ItemRarity): number {
  const costs: Record<ItemRarity, number> = {
    common: 30,
    uncommon: 45,
    rare: 60,
    epic: 120,
    legendary: 250,
  };
  return costs[rarity];
}

/** Get the vendor price for an item based on rarity */
function getVendorCost(rarity: ItemRarity): number {
  const costs: Record<ItemRarity, number> = {
    common: 50,
    uncommon: 75,
    rare: 100,
    epic: 200,
    legendary: 400,
  };
  return costs[rarity];
}

// --- Recalculate All Stats ---

export function recalculateStats(state: BullshiiiiSystemState): void {
  const stats = state.playerStats;

  // Reset to base values
  stats.maxHp = stats.baseMaxHp;
  stats.damage = stats.baseDamage;
  stats.armor = stats.baseArmor;
  stats.moveSpeed = stats.baseMoveSpeed;
  stats.attackSpeed = stats.baseAttackSpeed;
  stats.attackRange = stats.baseAttackRange;
  stats.critChance = stats.baseCritChance;
  stats.luck = stats.baseLuck;
  stats.xpGain = stats.baseXpGain;
  stats.pickupRange = stats.basePickupRange;
  stats.dodgeChance = stats.baseDodgeChance;
  stats.accuracy = stats.baseAccuracy;
  stats.damageReduction = stats.baseDamageReduction;
  stats.hpRegen = stats.baseHpRegen;
  stats.shieldRegen = stats.baseShieldRegen;
  stats.critDamage = stats.baseCritDamage;
  stats.difficulty = 1.0;

  // Collect all flat and percent modifiers
  const flatMods: Record<string, number> = {};
  const percentMods: Record<string, number> = {};

  for (const slot of state.inventory) {
    const stacks = slot.stacks;
    // BETA 20.78 — luck scales item buff VALUES (Dylan). _scaledMult is the
    // sum of per-pickup luck multipliers (snapshot at grab time). The luck
    // STAT itself is EXCLUDED (uses raw `stacks`) so a luck item can't feed
    // its own scaling — the exact runaway pattern the god-mode audit killed.
    // Undefined luckMultSum (legacy slot) falls back to `stacks` = old behavior.
    const _scaledMult = slot.luckMultSum ?? stacks;
    for (const mod of slot.item.statModifiers) {
      const key = mod.stat;
      const _eff = key === 'luck' ? stacks : _scaledMult;
      if (mod.isPercent) {
        percentMods[key] = (percentMods[key] || 0) + mod.value * _eff;
      } else {
        flatMods[key] = (flatMods[key] || 0) + mod.value * _eff;
      }
    }
    // BETA 16.30 — PSBOT cycle-30e Phase 2: ALL stackable items with
    // buffDimensions get per-dimension contributions added to the
    // modifier dicts automatically. Dimensions targeting standard
    // player stats (damage, attackSpeed, luck, armor, critChance,
    // critDamage, etc.) flow through here. Dimensions targeting
    // mechanic-specific fields (SHIT's puddleRadius, item-specific
    // mechanics) get read directly by their engine apply paths.
    if (slot.item.buffDimensions && slot.pickedDimensions) {
      for (const dim of slot.item.buffDimensions) {
        const picks = slot.pickedDimensions[dim.statField] ?? 0;
        if (picks <= 0) continue;
        const contribution = picks * dim.bonusPerStack;
        // unit='pct' → percent modifier; everything else → flat (engine
        // mechanic-specific dims like puddleRadius will be read by the
        // item's own apply path, not from these dicts).
        if (dim.unit === 'pct') {
          percentMods[dim.statField] = (percentMods[dim.statField] || 0) + contribution;
        } else {
          flatMods[dim.statField] = (flatMods[dim.statField] || 0) + contribution;
        }
      }
    }
  }

  // Apply flat modifiers first, then percent
  const applyMods = (statName: string, baseValue: number): number => {
    let value = baseValue + (flatMods[statName] || 0);
    value *= 1 + (percentMods[statName] || 0) / 100;
    return value;
  };

  stats.maxHp = Math.round(applyMods('maxHp', stats.baseMaxHp));
  stats.damage = Math.round(applyMods('damage', stats.baseDamage));
  stats.armor = Math.round(applyMods('armor', stats.baseArmor));
  stats.moveSpeed = Math.round(applyMods('moveSpeed', stats.baseMoveSpeed));
  stats.attackSpeed = parseFloat(applyMods('attackSpeed', stats.baseAttackSpeed).toFixed(2));
  stats.attackRange = Math.round(applyMods('attackRange', stats.baseAttackRange));
  stats.critChance = Math.min(100, applyMods('critChance', stats.baseCritChance));
  stats.luck = applyMods('luck', stats.baseLuck);
  stats.xpGain = parseFloat(applyMods('xpGain', stats.baseXpGain).toFixed(2));
  stats.pickupRange = Math.round(applyMods('pickupRange', stats.basePickupRange));
  // BETA 21.41 — dodgeChance + damageReduction base is 0, so the multiplicative
  // applyMods made percent modifiers do NOTHING ((0+0)*(1+12/100)=0). Sum additively
  // like critDamage (line 647) so C09 "+12% dodge" / Mystery Meat "+5% DR" actually land.
  stats.dodgeChance = Math.min(75, stats.baseDodgeChance + (flatMods.dodgeChance ?? 0) + (percentMods.dodgeChance ?? 0)); // Cap at 75%
  stats.accuracy = Math.max(25, applyMods('accuracy', stats.baseAccuracy)); // Floor at 25%
  stats.damageReduction = Math.min(80, stats.baseDamageReduction + (flatMods.damageReduction ?? 0) + (percentMods.damageReduction ?? 0)); // Cap at 80%
  stats.hpRegen = applyMods('hpRegen', stats.baseHpRegen);
  stats.shieldRegen = applyMods('shieldRegen', stats.baseShieldRegen);
  // BETA 16.37 — critDamage uses additive accumulation (player.critDamage
  // is itself a percent stat with base 0; multiplicative apply produces 0
  // because (0 + flat) × (1 + pct/100) = 0 when flat=0). Sum BOTH flat +
  // percent contributions directly. E04 stack of 1 = +20, stack of 3 = +60.
  stats.critDamage = stats.baseCritDamage + (flatMods.critDamage ?? 0) + (percentMods.critDamage ?? 0);
  stats.difficulty = applyMods('difficulty', 1.0);
  // BETA 19.98 — Gold multiplier aggregation. Previously items declaring
  // 'goldMultiplier' as their stat key were silent no-ops because the
  // aggregator had no entry for it. Now items can use 'gold' as the stat key
  // (e.g. item_write_that_down +15% gold per stack). Consumer in
  // itemsAdapter.ts:1838 multiplies into p.goldMultiplier.
  stats.gold = applyMods('gold', 1.0);

  // Clamp HP to new max
  if (stats.currentHp > stats.maxHp + stats.overheal) {
    stats.currentHp = stats.maxHp + stats.overheal;
  }
}

// --- Trigger Effects ---

export interface EffectTriggerResult {
  effectId: string;
  triggered: boolean;
  itemName: string;
  stacks: number;
}

/**
 * Fire all effects of a given trigger type across all inventory items.
 * Returns which effects triggered (for the engine to execute the actual logic).
 */
export function fireEffects(
  state: BullshiiiiSystemState,
  triggerType: TriggerType
): EffectTriggerResult[] {
  const results: EffectTriggerResult[] = [];

  for (const slot of state.inventory) {
    for (const effect of slot.item.effects) {
      if (effect.trigger !== triggerType) continue;

      const triggered = Math.random() < effect.chance;
      results.push({
        effectId: effect.effectId,
        triggered,
        itemName: slot.item.name,
        stacks: slot.stacks,
      });
    }
  }

  return results;
}

// --- Display Helpers ---

export interface InventoryDisplay {
  itemId: string;
  name: string;
  description: string;
  flavorText: string;
  rarity: ItemRarity;
  iconUrl: string;
  stacks: number;
  maxStacks: number;
  isMaxStacks: boolean;
}

/**
 * Get display data for the full inventory (for HUD rendering).
 */
export function getInventoryDisplay(state: BullshiiiiSystemState): InventoryDisplay[] {
  return state.inventory.map(slot => ({
    itemId: slot.item.id,
    name: slot.item.name,
    description: slot.item.description,
    flavorText: slot.item.flavorText,
    rarity: slot.item.rarity,
    iconUrl: slot.item.iconUrl,
    stacks: slot.stacks,
    maxStacks: slot.item.maxStacks,
    isMaxStacks: slot.item.maxStacks > 0 && slot.stacks >= slot.item.maxStacks,
  }));
}

/**
 * Get a summary of all stat modifiers currently active.
 */
export function getActiveModifiersSummary(state: BullshiiiiSystemState): Record<string, { flat: number; percent: number }> {
  const summary: Record<string, { flat: number; percent: number }> = {};

  for (const slot of state.inventory) {
    for (const mod of slot.item.statModifiers) {
      if (!summary[mod.stat]) {
        summary[mod.stat] = { flat: 0, percent: 0 };
      }
      if (mod.isPercent) {
        summary[mod.stat].percent += mod.value * slot.stacks;
      } else {
        summary[mod.stat].flat += mod.value * slot.stacks;
      }
    }
  }

  return summary;
}

/**
 * Count total unique items and total stacks in inventory.
 */
export function getInventoryStats(state: BullshiiiiSystemState): {
  uniqueItems: number;
  totalStacks: number;
  byRarity: Record<ItemRarity, number>;
} {
  const byRarity: Record<ItemRarity, number> = { common: 0, uncommon: 0, rare: 0, epic: 0, legendary: 0 };
  let totalStacks = 0;

  for (const slot of state.inventory) {
    byRarity[slot.item.rarity]++;
    totalStacks += slot.stacks;
  }

  return {
    uniqueItems: state.inventory.length,
    totalStacks,
    byRarity,
  };
}
